import React, { useState } from "react";
import { Layout } from "components/Layout.tsx/Layout";
import {
  PageContainer,
  Title,
  Subtitle,
  ImageGrid,
  ImageBlock,
  ImageStyled,
  DownloadBox,
  DownloadIconStyled,
  DownloadTitle,
  Description,
  DownloadVersion,
} from "./styles";
import { Footer } from "components/Footer/Footer";
import { useAxiosPrivate } from "hooks/api/useAxiosPrivate";
import { useMutation, useQuery } from "react-query";
import { AxiosError } from "axios";
import Loader from "components/Loader/Loader";

export const GettingStarted: React.FC = () => {
  const axiosPrivate = useAxiosPrivate();
  const [platformDownloading, setPlatformDownloading] = useState<
    "windows" | "macos" | null
  >(null);

  const {
    data: versionsData,
    isLoading: isVersionsLoading,
    isError,
    error,
  } = useQuery(["get-app-versions"], async () => {
    const [windowsRes, macosRes] = await Promise.all([
      axiosPrivate.get("/version-info/windows"),
      axiosPrivate.get("/version-info/macos"),
    ]);

    return {
      windows: windowsRes?.data?.current_version,
      macos: macosRes?.data?.current_version,
    };
  });

  const windowsVersion = versionsData?.windows || "unknown";
  const macosVersion = versionsData?.macos || "unknown";

  const downloadMutation = useMutation<Blob, AxiosError, "windows" | "macos">(
    async (platform) => {
      const response = await axiosPrivate.get(`/download/dist/${platform}`, {
        responseType: "arraybuffer",
        headers: {
          'Content-Type': 'multipart/form-data'
        },
      });
      return response.data;
    },
    {
      onSuccess: (blob, platform) => {
          const filename =
            platform === "windows"
              ? `ACF-Windows-v${windowsVersion}_Installer.zip`
              : `ACF-Macos-v${macosVersion}_Installer.zip`;
        
          const fileBlob = new Blob([blob], { type: "application/octet-stream" });
          const blobUrl = URL.createObjectURL(fileBlob);
        
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
        
          URL.revokeObjectURL(blobUrl);
          setPlatformDownloading(null);
      },
      onError: (error) => {
        alert("Download failed: " + error.message);
        setPlatformDownloading(null);
      },
    }
  );

  const handleDownload = (platform: "windows" | "macos") => {
    setPlatformDownloading(platform);
    downloadMutation.mutate(platform);
  };

  if (downloadMutation.isLoading || isVersionsLoading) {
    return <Loader />;
  }

  return (
    <Layout>
      <PageContainer>
        <Title>Getting Started</Title>
        <Subtitle>Choose your platform and begin your journey</Subtitle>

        <ImageGrid>
          <ImageBlock>
            <ImageStyled src="/assets/windows-logo.png" alt="Windows" />
            <DownloadBox onClick={() => handleDownload("windows")}>
              <DownloadIconStyled />
              <DownloadTitle>
                {platformDownloading === "windows"
                  ? "Downloading..."
                  : "Download for Windows"}
              </DownloadTitle>
            </DownloadBox>
            <DownloadVersion>
              Latest version: <b>{windowsVersion}</b>
            </DownloadVersion>
          </ImageBlock>

          <ImageBlock>
            <ImageStyled src="/assets/mac-logo.png" alt="Mac" />
            <DownloadBox onClick={() => handleDownload("macos")}>
              <DownloadIconStyled />
              <DownloadTitle>
                {platformDownloading === "macos"
                  ? "Downloading..."
                  : "Download for macOS"}
              </DownloadTitle>
            </DownloadBox>
            <DownloadVersion>
              Latest version: <b>{macosVersion}</b>
            </DownloadVersion>
          </ImageBlock>
        </ImageGrid>

        <Description>
          After downloading, follow the installation instructions to complete
          setup and access your dashboard.
        </Description>
      </PageContainer>
      <Footer />
    </Layout>
  );
};
